import { requestAccountDeletion } from "@serophin/nottldr-shared";
import { useState } from "preact/hooks";
import { useToast } from "../../contexts/ToastContext";
import { Modal } from "../Modal";

const CONFIRMATION = "delete";

const useRequestDeletion = (onClose: () => void) => {
  const toast = useToast();
  const [typed, setTyped] = useState("");
  const [sending, setSending] = useState(false);

  const close = () => {
    setTyped("");
    onClose();
  };

  const send = async () => {
    if (typed !== CONFIRMATION) {
      return;
    }
    setSending(true);
    try {
      await requestAccountDeletion("web");
      toast.success("Check your email for a deletion confirmation link");
      close();
    } catch (error) {
      console.error("Failed to request account deletion:", error);
      toast.error("Failed to request account deletion. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return { typed, setTyped, sending, close, send };
};

/** Asks the visitor to type "delete", then has the server email a confirmation link; nothing is deleted until the link is used. */
export const DeleteAccountModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const { typed, setTyped, sending, close, send } = useRequestDeletion(onClose);

  return (
    <Modal isOpen={isOpen} onClose={close}>
      <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">Delete Account</h2>
      <p class="text-gray-600 dark:text-gray-300 mb-4">
        This action is <strong>permanent and cannot be undone</strong>. All your data including saved articles, reading history, preferences, and followed
        authors will be permanently deleted.
      </p>
      <p class="text-gray-600 dark:text-gray-300 mb-4">
        Type <strong class="text-red-600">{CONFIRMATION}</strong> to confirm:
      </p>
      <input
        type="text"
        value={typed}
        onInput={(event) => setTyped(event.currentTarget.value)}
        placeholder={`Type ${CONFIRMATION}`}
        class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-neutral-700 text-gray-900 dark:text-white mb-4 focus:outline-none focus:ring-2 focus:ring-red-500"
      />
      <div class="flex gap-3 justify-end">
        <button
          type="button"
          onClick={close}
          class="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={send}
          disabled={typed !== CONFIRMATION || sending}
          class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {sending ? "Sending..." : "Delete Account"}
        </button>
      </div>
    </Modal>
  );
};
